import { Box, Divider, IconButton, List, ListItem, Paper, Popover, TextField, Typography, useTheme } from "@material-ui/core";
import { CalendarToday } from "@material-ui/icons";
import { addMonths } from "date-fns";
import { Fragment, memo } from "react";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";
import { ICustomDatePickerProps } from "./DashboardDatePicker";
import { dateFormatDateRange, dateFormatMonthRange, dateMaskFormat, getOptionsToExclude, maskDates, maskMonths } from "./DatePicker";
import DatePickerErrorMessage from "./ErrorMessage";
import Month from "./Month";
import { caseEventLogOptions, options } from "./utils";

function CustomDatePicker(props: ICustomDatePickerProps) {
	const { t } = useTranslation();
	const { palette, spacing } = useTheme();
	const dateFormat = dateMaskFormat(!!props.selectMonths, dateFormatMonthRange, dateFormatDateRange);
	const mask = dateMaskFormat(!!props.selectMonths, maskMonths, maskDates);

	const cohorts: Array<string> = [];
	const rangeOptions = props.isCaseEventLogFilter ? caseEventLogOptions : options;
	const optionsToExclude = getOptionsToExclude(props.contractStartDate || new Date());
	const filteredOptions = props.contractStartDate ? rangeOptions.filter((o) => !optionsToExclude.includes(o)) : rangeOptions;
	const enabledOptions = filteredOptions
		.filter((o) => (o !== "sinceStart" || !!props.contractStartDate) && (o !== "allTime" || props.isAllTimeOptionAvailable))
		.concat(cohorts);

	const months = [props.state.firstCalendarDate, addMonths(props.state.firstCalendarDate, 1)];
	const isValidationError =
		props.error || (!!props.state.startDate && !!props.state.endDate && props.state.startDate > props.state.endDate);

	return (
		<Paper
			variant="outlined"
			style={{
				padding: spacing(1, 2),
				width: props.selectMonths ? "30ch" : "36ch",
				position: "relative",
			}}
		>
			<Typography
				variant="caption"
				style={{
					paddingLeft: spacing(0.8),
					paddingRight: spacing(0.8),
					color: palette.primary.main,
					position: "absolute",
					left: "10px",
					top: "-8px",
					fontSize: "10px",
					background: props.labelBg ? props.labelBg : "linear-gradient(rgba(251,248,245,1) 50%, white 50%)",
					...props.labelStyles,
				}}
			>
				{t(props.label || "time-period")}
				{props.required && (
					<Typography variant="caption" style={{ color: palette.primary.main, marginLeft: "2px" }}>
						*
					</Typography>
				)}
			</Typography>
			<DatePickerErrorMessage
				isValidationError={isValidationError}
				startDateInvalid={props.startDateInvalid}
				endDateInvalid={props.endDateInvalid}
				label={props.label}
			/>
			<Box display="flex" justifyContent="space-between" alignItems="center">
				<InputMask
					mask={mask}
					placeholder={dateFormat.toLowerCase()}
					maskPlaceholder={dateFormat.toLowerCase()}
					value={props.state.rawStartValue}
					onChange={props.handleChangeStartDate}
					onBlur={() => props.handleLeaveFocus("rawStartValue")}
				>
					<TextField
						title="date-range-input"
						variant="standard"
						label=""
						helperText=""
						InputProps={{
							disableUnderline: true,
						}}
						error={false}
					/>
				</InputMask>
				<Box marginRight={2}>—</Box>
				<InputMask
					mask={mask}
					maskPlaceholder={dateFormat.toLowerCase()}
					placeholder={dateFormat.toLowerCase()}
					value={props.state.rawEndValue}
					onChange={props.handleChangeEndDate}
					onBlur={() => props.handleLeaveFocus("rawEndValue")}
				>
					<TextField
						title="date-range-input"
						variant="standard"
						label=""
						helperText=""
						error={false}
						InputProps={{
							disableUnderline: true,
						}}
					/>
				</InputMask>
				<IconButton size="small" onClick={props.handleClick}>
					<CalendarToday />
				</IconButton>
			</Box>
			<Popover
				onClose={props.handleClose}
				PaperProps={{
					style: {
						display: "flex",
						border: "none",
						boxShadow: "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
						borderRadius: "10px",
					},
				}}
				open={!!props.state.anchorEl}
				anchorEl={props.state.anchorEl}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
			>
				{!props.selectMonths && (
					<Box
						display="flex"
						justifyContent="space-between"
						minWidth="520px"
						alignItems="flex-start"
						height="324px"
						padding={"24px"}
					>
						{months.map((d, i) => (
							<Month
								key={`${d.getTime()}.${i}`}
								year={d.getFullYear()}
								month={d.getMonth()}
								startDate={props.state.startDate}
								endDate={props.state.endDate}
								index={i}
								goToNextMonths={props.handleGoToNextMonth}
								goToPreviousMonths={props.handleGoToPreviousMonths}
								handleSelectDay={props.handleSelectDate}
							/>
						))}
					</Box>
				)}
				<Divider orientation="vertical" flexItem />
				<List
					style={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
						minWidth: "180px",
						justifyContent: "space-between",
						overflow: "hidden",
						borderRadius: "0 10px 10px 0",
						paddingBottom: "7px",
					}}
				>
					{enabledOptions.map((o, i) => (
						<Fragment key={i}>
							<ListItem
								selected={props.state.option === o}
								style={{
									marginBottom: i === enabledOptions.length - 1 ? "-8px" : "0px",
									height: "100%",
									color: props.state.option === o ? palette.primary.main : palette.text.primary,
								}}
								onClick={() => props.handleClickOption(o)}
								button
							>
								{t(o)}
							</ListItem>
							{i < enabledOptions.length - 1 && <Divider />}
						</Fragment>
					))}
				</List>
			</Popover>
		</Paper>
	);
}

export default memo(CustomDatePicker);
